import { Component, effect, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { LayoutComponent } from './shared/components/layout/layout.component';
import { HeaderComponent } from './shared/components/header/header.component';
import { ImpersonationBannerComponent } from './shared/components/impersonation-banner/impersonation-banner.component';
import { filter, Subscription } from 'rxjs';
import { Menu } from './shared/components/menu/models/menu';
import { PermissionService } from './shared/services/permission/permission.service';
import { PermissionEnum, ResourceEnum } from '../environments/shared-environment/permission';
import { PageKey } from './shared/services/page/models/page.key';
import { ProgressComponent } from './shared/components/export/progress.component';
import { ThemeService } from './shared/services/theme/theme.service';
import { MeService } from './shared/services/me/me.service';
import { ToastModule } from 'primeng/toast';
import { GlobalSpinnerService } from './shared/services/global-spinner/global-spinner.service';
import { ParamsService } from './shared/services/params/params.service';
import { ButtonModule } from 'primeng/button';
import { ToastComponent } from './shared/components/toast/toast.component';
import { GlobalCacheConfig } from 'ts-cacheable';
import { PrimeNG } from 'primeng/config';
import { Liga } from "../themes/presets/liga";
import { ModalStatusComponent } from './shared/modals/modal-status/modal-status.component';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [RouterOutlet, LayoutComponent, HeaderComponent, ProgressComponent, ToastModule, ButtonModule, ToastComponent, ModalStatusComponent, ImpersonationBannerComponent],
    template: `
    <div class="flex h-screen w-full flex-col overflow-hidden">
      <l-impersonation-banner />
      <l-layout class="flex min-h-0 flex-1 flex-col" [fullscreen]="fullscreen" [useBackground]="showBackground" [showFooter]="showFooter">
        @if (showHeader) {
          <l-header class="block w-full h-auto" header />
        }

        <router-outlet />
      </l-layout>
    </div>

    <l-toast />
    <l-progress />
    <l-modal-status />
  `
})
export class AppComponent implements OnInit, OnDestroy {

  constructor(
    params: ParamsService,
    themeService: ThemeService,
    globalSpinnerService: GlobalSpinnerService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly primeng: PrimeNG,
    private readonly meService: MeService,
    private readonly permissionService: PermissionService,
  ) {
    params.initialize();
    themeService.initialize();
    globalSpinnerService.initialize();
    this.handleRouteChange();

    GlobalCacheConfig.maxAge = 3600000;
    GlobalCacheConfig.maxCacheCount = 100;

    this.primeng.theme.set({
      preset: Liga,
      options: {
        darkModeSelector: 'dark',
        cssLayer: {
          name: 'primeng',
          order: 'reset, base, primeng, utilities'
        },
      /*  ripple: true,
        mergeSections: true*/
      }
    });

    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.handleRouteChange();
        this.scrollToTop();
      });

    effect(() => {
      meService.me();
      this.checkMenu();
    });
  }

  @ViewChild(LayoutComponent)
  private readonly layoutComponent?: LayoutComponent;

  public fullscreen: boolean = false;

  public showHeader: boolean = false;

  public showFooter: boolean = false;

  public showBackground: boolean = false;

  public menus: Menu[] = [];

  public HIDDEN_HEADER_ROUTES: string[] = [
    'login',
    'forgot/password',
    'reset/password',
    'first-access'
  ];

  private readonly routerSubscription?: Subscription;

  public async ngOnInit(): Promise<void> {
    await this.meService.storageMe();
  }

  public ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
  }

  private checkMenu(): void {
    const can = (resource: ResourceEnum, permission: PermissionEnum, menu: Menu | Menu[]): Menu[] => {
      if (!this.permissionService.has(resource, permission)) {
        return [];
      }

      return Array.isArray(menu) ? menu : [menu];
    };

    this.menus = [
      {
        icon: 'home',
        title: 'Home',
        subtitle: 'Página inicial',
        link: '/home',
        key: PageKey.HOME,
      },
      ...can(ResourceEnum.USERS, PermissionEnum.VIEW, {
        icon: 'group',
        title: 'Usuários',
        subtitle: 'Gerencie usuários',
        link: '/users',
        key: PageKey.USERS,
        submenus: [
          ...can(ResourceEnum.USERS, PermissionEnum.VIEW, [
            { title: 'Listar', link: '/users', key: PageKey.USERS },
            { title: 'Criar', link: '/users/crud', key: PageKey.CRUD }
          ]),
        ]
      }),
    ];
  }

  private handleRouteChange(): void {
    const currentUrl = this.router.url.split('?')[0].split('#')[0];

    const activeRoute = this.getDeepestRoute(this.route);
    const routeData = activeRoute.snapshot.data;

    this.updateDocumentTitle(routeData['title']);
    this.fullscreen = !!routeData['fullscreen'];
    const hideHeaderByRoute = !!routeData['hideHeader'];
    this.showHeader = !hideHeaderByRoute && !this.HIDDEN_HEADER_ROUTES.some(route => this.router.url.includes(route));
    this.showFooter = this.showHeader && !/^\/courses\/[^/]+\/content(\/|$)/.test(currentUrl);
    this.showBackground = /^\/home(\/|$)/.test(currentUrl);
  }

  private scrollToTop(): void {
    requestAnimationFrame(() => {
      this.layoutComponent?.scrollToTop();
    });
  }

  private getDeepestRoute(route: ActivatedRoute): ActivatedRoute {
    let deepest = route;
    while (deepest.firstChild) {
      deepest = deepest.firstChild;
    }
    return deepest;
  }

  private updateDocumentTitle(pageTitle?: string): void {
    document.title = pageTitle ? `${pageTitle} | Plataforma` : 'Unico';
  }

}
